import { useCallback } from 'react'
import { FilterState, FilterableField } from '@/components/filters/types'
import CollapsibleSection from './CollapsibleSection'
import AudienceSection from './AudienceSection'
import TriggerSection from './TriggerSection'
import EmailConfigPanel from './EmailConfigPanel'
import EmailPreview from './EmailPreview'
import { uploadImage } from '@/lib/imageUpload'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface PageRule {
  match_type: 'exact' | 'contains' | 'starts_with' | 'regex'
  value: string
}

interface EmailFormData {
  name: string
  title: string
  body: string
  sent_by_agent_id: number
  email_subject: string
  email_from_name: string
  email_reply_to: string
  audience_type: 'dynamic' | 'fixed'
  audience_rules: FilterState
  trigger_type: 'event' | 'scheduled' | 'page' | 'all_pages'
  trigger_event: string
  trigger_page_rules: PageRule[]
  scheduled_at: string
  delay_seconds: number
  frequency_cap: string
}

interface EmailFormLayoutProps {
  data: EmailFormData
  setData: <K extends keyof EmailFormData>(key: K, value: EmailFormData[K]) => void
  errors: Partial<Record<string, string>>
  agents: Agent[]
  filterableFields: FilterableField[]
  addPageRule: () => void
  updatePageRule: (index: number, field: keyof PageRule, value: string) => void
  removePageRule: (index: number) => void
}

export default function EmailFormLayout({
  data,
  setData,
  errors,
  agents,
  filterableFields,
  addPageRule,
  updatePageRule,
  removePageRule,
}: EmailFormLayoutProps) {
  const selectedAgent = agents.find((a) => a.id === data.sent_by_agent_id)

  const handleImageUpload = useCallback(async (file: File): Promise<string> => {
    const result = await uploadImage(file)
    return result.url
  }, [])

  return (
    <div className="space-y-6">
      <CollapsibleSection title="Conteúdo">
        <div className="border-t border-gray-200">
          <div className="flex flex-1" style={{ minHeight: '500px' }}>
            <EmailConfigPanel
              agents={agents}
              sentByAgentId={data.sent_by_agent_id}
              onAgentChange={(id) => setData('sent_by_agent_id', id)}
              emailFromName={data.email_from_name}
              onFromNameChange={(v) => setData('email_from_name', v)}
              emailReplyTo={data.email_reply_to}
              onReplyToChange={(v) => setData('email_reply_to', v)}
              name={data.name}
              onNameChange={(v) => setData('name', v)}
              nameError={errors.name}
            />
            <div className="flex-1 p-4 bg-gray-50">
              <EmailPreview
                subject={data.title}
                onSubjectChange={(v) => {
                  setData('title', v)
                  setData('email_subject', v)
                }}
                body={data.body}
                onBodyChange={(v) => setData('body', v)}
                agent={selectedAgent}
                onImageUpload={handleImageUpload}
              />
              {errors.body && (
                <p className="mt-2 text-sm text-red-600">{errors.body}</p>
              )}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Público">
        <AudienceSection
          audienceType={data.audience_type}
          onAudienceTypeChange={(v) => setData('audience_type', v)}
          audienceRules={data.audience_rules}
          onAudienceRulesChange={(state) => setData('audience_rules', state)}
          filterableFields={filterableFields}
        />
      </CollapsibleSection>

      <CollapsibleSection title="Gatilho">
        <TriggerSection
          idPrefix="email"
          triggerType={data.trigger_type}
          onTriggerTypeChange={(v) => setData('trigger_type', v)}
          triggerEvent={data.trigger_event}
          onTriggerEventChange={(v) => setData('trigger_event', v)}
          triggerPageRules={data.trigger_page_rules}
          addPageRule={addPageRule}
          updatePageRule={updatePageRule}
          removePageRule={removePageRule}
          scheduledAt={data.scheduled_at}
          onScheduledAtChange={(v) => setData('scheduled_at', v)}
          delaySeconds={data.delay_seconds}
          onDelaySecondsChange={(v) => setData('delay_seconds', v)}
          frequencyCap={data.frequency_cap}
          onFrequencyCapChange={(v) => setData('frequency_cap', v)}
          delayHelpText="Aguardar estes segundos após o gatilho antes de enviar o e-mail"
          errors={errors}
        />
      </CollapsibleSection>
    </div>
  )
}
